import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';
import {mapRealtimePayloadToViewModel, SOURCES_PRESET_FOLDERS, SOURCES_ROOT_VIEW} from './sourcesAdapter';

const SOURCES_URL_PARAMS = Object.freeze({
    view: 'sourcesView',
    branch: 'sourcesBranch',
    path: 'sourcesPath',
});

function normalizeString(value, fallback = null) {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    return String(value);
}

function updateUrlParams(updates) {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);

    Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, String(value));
        }
    });

    window.history.replaceState({}, '', url.toString());
}

function getPathEntriesForView(view) {
    const pathIds = Array.isArray(view?.pathIds) ? view.pathIds : [];
    const pathNames = Array.isArray(view?.pathNames) ? view.pathNames : [];

    if (pathIds.length === 0) {
        return [];
    }

    return pathIds.map((id, index) => ({
        id,
        name: pathNames[index] || (index === pathIds.length - 1 ? view?.name || 'Untitled folder' : `Folder ${index + 1}`),
    }));
}

function serializeActiveView(activeView) {
    if (!activeView || activeView.type === 'root') {
        return {
            [SOURCES_URL_PARAMS.view]: null,
            [SOURCES_URL_PARAMS.branch]: null,
            [SOURCES_URL_PARAMS.path]: null,
        };
    }

    if (activeView.type === 'preset') {
        return {
            [SOURCES_URL_PARAMS.view]: 'preset',
            [SOURCES_URL_PARAMS.branch]: activeView.id || activeView.branch || null,
            [SOURCES_URL_PARAMS.path]: null,
        };
    }

    if (activeView.type === 'folder') {
        return {
            [SOURCES_URL_PARAMS.view]: 'folder',
            [SOURCES_URL_PARAMS.branch]: activeView.branch || null,
            [SOURCES_URL_PARAMS.path]: JSON.stringify(getPathEntriesForView(activeView)),
        };
    }

    return {
        [SOURCES_URL_PARAMS.view]: null,
        [SOURCES_URL_PARAMS.branch]: null,
        [SOURCES_URL_PARAMS.path]: null,
    };
}

function deserializeActiveView(searchParams) {
    const viewType = normalizeString(searchParams.get(SOURCES_URL_PARAMS.view), 'root');

    if (viewType === 'preset') {
        const presetId = normalizeString(searchParams.get(SOURCES_URL_PARAMS.branch));
        const preset = SOURCES_PRESET_FOLDERS.find((folder) => folder.id === presetId);
        return preset
            ? {type: 'preset', id: preset.id, name: preset.name, branch: preset.id}
            : SOURCES_ROOT_VIEW;
    }

    if (viewType === 'folder') {
        const branch = normalizeString(searchParams.get(SOURCES_URL_PARAMS.branch));
        const rawPath = normalizeString(searchParams.get(SOURCES_URL_PARAMS.path));

        if (!rawPath) {
            return SOURCES_ROOT_VIEW;
        }

        try {
            const parsedPath = JSON.parse(rawPath);
            const pathEntries = Array.isArray(parsedPath)
                ? parsedPath.filter((entry) => normalizeString(entry?.id))
                : [];

            if (pathEntries.length === 0) {
                return SOURCES_ROOT_VIEW;
            }

            const pathIds = pathEntries.map((entry) => String(entry.id));
            const pathNames = pathEntries.map((entry, index) => normalizeString(entry?.name, `Folder ${index + 1}`));

            return {
                type: 'folder',
                id: pathIds[pathIds.length - 1],
                name: pathNames[pathNames.length - 1],
                branch,
                pathIds,
                pathNames,
            };
        } catch (error) {
            console.warn('[SourcesExplorerStore] Failed to parse sourcesPath from URL', error);
            return SOURCES_ROOT_VIEW;
        }
    }

    return SOURCES_ROOT_VIEW;
}

const createInitialState = () => ({
    inputValue: '',
    debouncedQuery: '',
    activeView: SOURCES_ROOT_VIEW,
    sources: mapRealtimePayloadToViewModel(SOURCES_ROOT_VIEW, null),
    connectionState: 'idle',
    isLoading: false,
    error: null,
    warning: null,
    isStale: false,
    // Federated unified search (M6)
    searchResults: [],
    searchMeta: null,
    isSearching: false,
    searchError: null,
    // Per-scope hit counts derived from a parallel all-sources background query
    scopeCounts: null,
    isScopeCounting: false,
    _initialized: false,
});

const useSourcesExplorerStore = create(
    subscribeWithSelector((set, get) => ({
        ...createInitialState(),
        setInputValue: (inputValue) => set({inputValue}),
        setDebouncedQuery: (debouncedQuery) => set({debouncedQuery}),
        setSearchResults: (searchResults, searchMeta = null) => set({searchResults, searchMeta}),
        setIsSearching: (isSearching) => set({isSearching}),
        setSearchError: (searchError) => set({searchError}),
        clearSearch: () => set({searchResults: [], searchMeta: null, isSearching: false, searchError: null, scopeCounts: null, isScopeCounting: false}),
        setScopeCounts: (scopeCounts) => set({scopeCounts}),
        setIsScopeCounting: (isScopeCounting) => set({isScopeCounting}),
        setActiveView: (activeView) => set({activeView}),
        setSources: (sources) => set({sources}),
        setConnectionState: (connectionState) => set({connectionState}),
        setIsLoading: (isLoading) => set({isLoading}),
        setError: (error) => set({error}),
        setWarning: (warning) => set({warning}),
        setIsStale: (isStale) => set({isStale}),
        initFromUrl: (searchParams) => {
            if (get()._initialized) {
                return;
            }

            set({
                activeView: deserializeActiveView(searchParams),
                _initialized: true,
            });
        },
        reset: () => set(createInitialState()),
    }))
);

useSourcesExplorerStore.subscribe(
    (state) => state.activeView,
    (activeView) => {
        if (!useSourcesExplorerStore.getState()._initialized) {
            return;
        }

        updateUrlParams(serializeActiveView(activeView));
    },
    {
        equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
    }
);

export default useSourcesExplorerStore;

